/**
 * 試験結果確認ページ
 *************************************************/
import { GraphQLResult } from '@aws-amplify/api';
import { ListAnswersQuery, ListCategorysQuery } from 'API';
import { API, graphqlOperation } from 'aws-amplify';
import Footer from 'components/common/Footer';
import { listAnswers, listCategorys } from 'graphql/queries';
import { startLoading, endLoading } from 'modules/commonModule';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import DataTable from 'react-data-table-component';
import { useDispatch } from 'react-redux';
import { Button, Container } from 'semantic-ui-react';
import Header from '../common/Header';

type ScoreType = {
  userId: string;
  totalPoint: number;
  correctPoint: number;
  rank: string;
  totalAnswerCount: number;
  totalCorrectCount: number;
  correctRate: number;
};

const ScorePage: FC = () => {
  const dispatch = useDispatch();
  const [scoreListState, setScoreListState] = useState<ScoreType[]>([]);

  useEffect(() => {
    (async () => {
      try {
        dispatch(startLoading());

        // 大問題を取得
        const resCategorys = (await API.graphql(graphqlOperation(listCategorys))) as GraphQLResult<ListCategorysQuery>;
        const categorys = resCategorys.data?.listCategorys?.items;

        // 解答を取得
        const resAnswers = (await API.graphql(
          graphqlOperation(listAnswers, { limit: 10000 }),
        )) as GraphQLResult<ListAnswersQuery>;
        let nextToken = resAnswers.data?.listAnswers?.nextToken;
        const answers = resAnswers.data?.listAnswers?.items;

        // 次ページにも解答データが存在する場合は、解答データを追加取得する
        while (nextToken) {
          // eslint-disable-next-line no-await-in-loop
          const resNextAnswers = (await API.graphql(
            graphqlOperation(listAnswers, { limit: 10000, nextToken }),
          )) as GraphQLResult<ListAnswersQuery>;
          nextToken = resNextAnswers.data?.listAnswers?.nextToken;
          const nextAnswers = resNextAnswers.data?.listAnswers?.items;

          if (answers && nextAnswers) {
            answers.push(...nextAnswers);
          }
        }

        const scoreList: ScoreType[] = [];

        if (categorys && answers) {
          // ユーザーのリストを取得
          const users = answers.filter(
            (answer: any, index: number, self: any) => self.findIndex((e: any) => e.userId === answer.userId) === index,
          );

          if (users) {
            // ユーザー分 全ループ
            users.forEach((user) => {
              let totalPoint = 0; // 得点数（合計）
              let totalAnswerCount = 0; // 回答数（合計）
              let totalCorrectCount = 0; // 正答数（合計）

              // ループ中ユーザーの解答だけ抜き出し
              const targetAnswers = answers.filter((answer) => answer?.userId === user?.userId);

              if (user) {
                // 大問題ループ
                categorys.forEach((category) => {
                  let point = 0; // 得点数（カテゴリごと）
                  let answerCount = 0; // 回答数（カテゴリごと）
                  let correctCount = 0; // 正答数（カテゴリごと）

                  if (category) {
                    // 解答分ループ
                    targetAnswers.forEach((answer) => {
                      if (answer && answer.quiz) {
                        // ループ中の問題カテゴリと一致した解答のみ採点処理を実行
                        if (category?.id === answer.quiz.catergoryId) {
                          // 回答数
                          answerCount += 1;

                          if (answer.answer.toUpperCase() === answer.quiz.answer.toUpperCase() && answer.quiz.points) {
                            correctCount += 1;

                            point += answer.quiz.points;
                          }
                        }
                      }
                    });
                  }

                  totalPoint += point;
                  totalAnswerCount += answerCount;
                  totalCorrectCount += correctCount;
                });

                // 正答率（回答数 ÷ 正答数 × 100）
                const correctRate = (totalCorrectCount / totalAnswerCount) * 100;

                // 正答率点数
                let correctPoint;
                if (correctRate > 90) {
                  correctPoint = Math.floor((correctRate - 90) * 10 ** 1) / 10 ** 1;
                } else {
                  correctPoint = 0;
                }

                // 合計点数（各問題の得点数 + 正答率点数）
                totalPoint += correctPoint;

                // ランク
                let rank;
                if (totalPoint >= 140) rank = 'S';
                else if (totalPoint >= 115) rank = 'A';
                else if (totalPoint >= 100) rank = 'B';
                else if (totalPoint >= 80) rank = 'C';
                else if (totalPoint >= 65) rank = 'D';
                else rank = 'E';

                const userScore = {
                  userId: user.userId,
                  totalPoint,
                  correctPoint,
                  rank,
                  totalAnswerCount,
                  totalCorrectCount,
                  correctRate,
                };

                scoreList.push(userScore);
              }
            });

            setScoreListState(scoreList);
          }
        }
      } catch (e) {
        console.log(e);
      } finally {
        dispatch(endLoading());
      }
    })();
  }, [dispatch]);

  const columns = useMemo(() => {
    return [
      {
        name: 'ユーザーID',
        selector: 'userId',
        sortable: true,
        maxWidth: '150px',
      },
      {
        name: '合計点数',
        selector: 'totalPoint',
        sortable: true,
        maxWidth: '250px',
      },
      {
        name: '正答率点数',
        selector: 'correctPoint',
        sortable: true,
        maxWidth: '150px',
      },
      {
        name: 'ランク',
        selector: 'rank',
        sortable: true,
        maxWidth: '150px',
      },
      {
        name: '回答数',
        selector: 'totalAnswerCount',
        sortable: true,
        maxWidth: '150px',
      },
      {
        name: '正答数',
        selector: 'totalCorrectCount',
        sortable: true,
        maxWidth: '150px',
      },
      {
        name: '正答率',
        selector: 'correctRate',
        sortable: true,
        maxWidth: '150px',
      },
    ];
  }, []);

  /**
   * CSVヘッダー
   */
  const csvHeader = useMemo(() => {
    return columns
      .filter((column) => column.name)
      .map((column) => {
        return {
          label: column.name,
          key: column.selector,
        };
      });
  }, [columns]);

  /**
   * CSVデータ
   */
  const csvData = useMemo(() => {
    return scoreListState.map((score) => {
      return {
        userId: score.userId,
        totalPoint: score.totalPoint,
        correctPoint: score.correctPoint,
        rank: score.rank,
        totalAnswerCount: score.totalAnswerCount,
        totalCorrectCount: score.totalCorrectCount,
        correctRate: score.correctRate,
      };
    });
  }, [scoreListState]);

  return (
    <>
      <Header />
      <Container style={{ marginTop: '50px', marginBottom: '70px' }}>
        <Button primary>
          <CSVLink style={{ color: '#fff' }} filename="試験結果.csv" headers={csvHeader} data={csvData}>
            CSV出力
          </CSVLink>
        </Button>
        <DataTable
          columns={columns}
          data={scoreListState}
          fixedHeader
          fixedHeaderScrollHeight="calc(100vh - 380px)"
          striped
          pagination
          noHeader
          paginationTotalRows={scoreListState?.length}
          paginationPerPage={300}
          paginationRowsPerPageOptions={[300]}
          paginationComponentOptions={{ noRowsPerPage: true }}
          noDataComponent="表示するデータがありません"
        />
      </Container>
      <Footer />
    </>
  );
};
export default ScorePage;
